import { useEffect, useLayoutEffect, useRef } from "react";

/**
 * AutoScrollingPhone — a fixed-size phone bezel + notch that clips a tall
 * mobile-website composition inside its "screen" and gently auto-scrolls the
 * inner content from top → bottom → top on a slow ease-in-out loop, à la an
 * agency case-study preview.
 *
 * - The phone frame stays completely still — only the inner content moves.
 * - Uses the Web Animations API so scroll distance can be measured at runtime
 *   (content height varies with images loading + responsive width).
 * - Pauses on hover, respects `prefers-reduced-motion`, and pauses when
 *   scrolled off-screen (via IntersectionObserver) to save CPU/battery.
 *
 * Timings (per user brief): 3s pause at top → 15s scroll down → 2.5s pause
 * at bottom → 15s scroll back to top → repeat. Total loop: 35.5s.
 */
export const AutoScrollingPhone = ({ children, screenHeight = 340, className = "" }) => {
  const viewportRef = useRef(null);
  const contentRef = useRef(null);
  const animationRef = useRef(null);

  // Build (or rebuild) the WAAPI animation whenever the scroll distance changes.
  useLayoutEffect(() => {
    if (!contentRef.current || !viewportRef.current) return undefined;

    let cancelled = false;
    let lastDistance = -1;

    const build = () => {
      if (cancelled || !contentRef.current || !viewportRef.current) return;

      const contentH = contentRef.current.getBoundingClientRect().height;
      const viewH = viewportRef.current.getBoundingClientRect().height;
      const distance = Math.max(0, Math.round(contentH - viewH));
      if (distance <= 0) return; // nothing to scroll yet

      // Skip if the distance hasn't materially changed — avoids the ResizeObserver
      // firing on sub-pixel layout jitter and resetting the animation to offset 0.
      if (Math.abs(distance - lastDistance) < 4) return;
      lastDistance = distance;

      // Cancel any prior animation before starting a new one.
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }

      const anim = contentRef.current.animate(
        [
          { transform: "translateY(0)", offset: 0 },
          { transform: "translateY(0)", offset: 0.085 },             // ~3s pause at top
          { transform: `translateY(-${distance}px)`, offset: 0.5 },  // ~15s eased scroll down
          { transform: `translateY(-${distance}px)`, offset: 0.57 }, // ~2.5s pause at bottom
          { transform: "translateY(0)", offset: 1 },                 // ~15s eased scroll up
        ],
        {
          duration: 35500,
          iterations: Infinity,
          easing: "ease-in-out",
        },
      );

      // Respect user motion preference.
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        anim.pause();
      }

      animationRef.current = anim;
    };

    // Measure now, and again after images likely finished loading.
    build();
    const t1 = setTimeout(build, 800);
    const t2 = setTimeout(build, 2500);

    const ro = new ResizeObserver(build);
    ro.observe(contentRef.current);
    ro.observe(viewportRef.current);

    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      ro.disconnect();
      if (animationRef.current) {
        animationRef.current.cancel();
        animationRef.current = null;
      }
    };
  }, [children, screenHeight]);

  // Pause when off-screen to save battery / stop pointless work.
  useEffect(() => {
    if (!viewportRef.current) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        const anim = animationRef.current;
        if (!anim) return;
        if (entry.isIntersecting) anim.play();
        else anim.pause();
      },
      { threshold: 0.1 },
    );
    io.observe(viewportRef.current);
    return () => io.disconnect();
  }, []);

  const onEnter = () => animationRef.current?.pause();
  const onLeave = () => {
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      animationRef.current?.play();
    }
  };

  return (
    <div
      className={`rounded-[22px] bg-[#0A0A0A] p-[4px] shadow-[0_14px_28px_-14px_rgba(11,28,46,0.5)] ${className}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      data-testid="autoscroll-phone"
    >
      <div className="relative w-full rounded-[18px] overflow-hidden bg-white">
        {/* iPhone-style notch — sits above the scrolling screen */}
        <div className="absolute top-1 left-1/2 -translate-x-1/2 w-10 h-2.5 bg-black rounded-full z-20" />
        <div
          ref={viewportRef}
          className="relative overflow-hidden"
          style={{ height: `${screenHeight}px` }}
        >
          <div ref={contentRef} className="w-full will-change-transform">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
